/**
 * @fileoverview Wizard Progress Bar Component
 * @module WizardProgressBar
 * 
 * Business Context:
 * - Shows progress through the Build Your Case wizard phases
 * - Clean, minimal design as standalone header
 */

import React from 'react';
import { Check } from 'lucide-react';

export type WizardPhase = 'review' | 'confirm' | 'generate' | 'complete';

interface PhaseConfig {
  id: WizardPhase;
  label: string;
  number: number;
}

const PHASES: PhaseConfig[] = [
  { id: 'review', label: 'Review', number: 1 },
  { id: 'confirm', label: 'Confirm', number: 2 },
  { id: 'generate', label: 'Generate', number: 3 },
  { id: 'complete', label: 'Complete', number: 4 },
];

interface WizardProgressBarProps {
  currentPhase: WizardPhase;
  className?: string;
}

const WizardProgressBar: React.FC<WizardProgressBarProps> = ({ 
  currentPhase,
  className = ''
}) => {
  const currentIndex = PHASES.findIndex(p => p.id === currentPhase);

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 p-4 ${className}`}>
      {/* Progress Steps */}
      <div className="flex items-center justify-between max-w-lg mx-auto">
        {PHASES.map((phase, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;

          return (
            <React.Fragment key={phase.id}>
              {/* Step */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    isCompleted
                      ? 'bg-teal-600 text-white'
                      : isCurrent
                      ? 'bg-teal-600 text-white ring-2 ring-teal-200'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {isCompleted ? <Check className="w-4 h-4" /> : phase.number}
                </div>
                <span
                  className={`mt-1.5 text-xs font-medium ${
                    isCompleted || isCurrent ? 'text-teal-600' : 'text-slate-400'
                  }`}
                >
                  {phase.label}
                </span>
              </div>

              {/* Connector */}
              {index < PHASES.length - 1 && (
                <div className="flex-1 mx-3 h-0.5 bg-slate-100 relative -mt-5">
                  <div
                    className={`absolute inset-y-0 left-0 bg-teal-600 transition-all duration-300 ${
                      index < currentIndex ? 'w-full' : 'w-0'
                    }`}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default WizardProgressBar;
